import { useQuery } from "@tanstack/react-query";
import { useCallback } from "react";

import { useRestClient } from "@/lib/restClient";
import type { DashboardKind, DashboardRecord } from "@/lib/dashboard/types";
import { useOptionalAuthorization } from "@/contexts/AuthorizationContext";
import { useAccount } from "@/hooks/useAccount";
import { isDashboardVisibleToProfile } from "@/lib/dashboard/dashboardVisibility";

interface DashboardsResponse {
  dashboards: DashboardRecord[];
  count: number;
}

export type DashboardStateFilter = "all" | "draft" | "saved";
export type DashboardSort = "name" | "state" | "created_at" | "updated_at";
export type SortOrder = "asc" | "desc";
export type DashboardRelationship = "accessible" | "owned" | "shared";

export interface DashboardListQuery {
  kind?: DashboardKind;
  state?: DashboardStateFilter;
  sort?: DashboardSort;
  order?: SortOrder;
  relationship?: DashboardRelationship;
}

export function useDashboards(query: DashboardListQuery = {}) {
  const apiFetch = useRestClient();
  const profile = useOptionalAuthorization()?.profile;
  const account = useAccount().data;
  const accountId = account?.account_id;
  const demoEnabled = account?.demo_enabled === true;
  // A previewed client profile sees only what would be published to it (see dashboardVisibility).
  const select = useCallback(
    (dashboards: DashboardRecord[]) =>
      dashboards.filter(
        (dashboard) =>
          (dashboard.kind ?? "dashboard") === (query.kind ?? "dashboard") &&
          isDashboardVisibleToProfile(dashboard, profile, accountId, demoEnabled),
      ),
    [profile, accountId, demoEnabled, query.kind],
  );

  return useQuery({
    queryKey: ["dashboards", "admin", query],
    queryFn: async () => {
      const parameters = new URLSearchParams();
      if (query.kind) parameters.set("kind", query.kind);
      if (query.state && query.state !== "all") parameters.set("state", query.state);
      if (query.sort) parameters.set("sort", query.sort);
      if (query.order) parameters.set("order", query.order);
      if (query.relationship) parameters.set("relationship", query.relationship);
      const suffix = parameters.size > 0 ? `?${parameters.toString()}` : "";
      const data = await apiFetch<DashboardsResponse>(`/api/dashboards${suffix}`);
      return data.dashboards;
    },
    select,
    staleTime: 30_000,
  });
}
